import { EdgeBuffer } from './edgebuffer'
import { SettingsStore, SENSITIVITY } from './settings'

// Per-tick input snapshot. Plain data so it can be serialized for net play later.
export interface InputState {
  forward: boolean
  back: boolean
  left: boolean
  right: boolean
  jump: boolean
  jumpPressed: boolean // edge-triggered: true only on the tick jump went down
  crouch: boolean      // used for noclip down (and later: ducking)
  boost: boolean       // shift; speed-boosts noclip
  noclipPressed: boolean // edge-triggered: tick when V was pressed
  yaw: number          // radians; world rotation around +Y
  pitch: number        // radians; clamped to (-π/2, π/2)
  wantsReset: boolean
}

export interface InputController {
  state: InputState
  consumeTickEdges(): void
  isLocked(): boolean
  getSensitivity(): number
  setSensitivity(v: number): void
}

// CS:S-style sensitivity: degrees-per-pixel-per-count, matches m_yaw=0.022 default.
const M_YAW_RAD = (0.022 * Math.PI) / 180

export function createInput(canvas: HTMLCanvasElement, onLockChange?: (locked: boolean) => void): InputController {
  const state: InputState = {
    forward: false,
    back: false,
    left: false,
    right: false,
    jump: false,
    jumpPressed: false,
    crouch: false,
    boost: false,
    noclipPressed: false,
    yaw: 0,
    pitch: 0,
    wantsReset: false,
  }

  let noclipDown = false
  const edges = new EdgeBuffer(['jump', 'noclip'] as const)
  const settings = new SettingsStore()
  let sensitivity = settings.loadNumber(SENSITIVITY)

  const setKey = (code: string, down: boolean): boolean => {
    switch (code) {
      case 'KeyW': state.forward = down; return true
      case 'KeyS': state.back = down; return true
      case 'KeyA': state.left = down; return true
      case 'KeyD': state.right = down; return true
      case 'Space': state.jump = down; return true
      case 'KeyC': state.crouch = down; return true
      case 'ShiftLeft':
      case 'ShiftRight': state.boost = down; return true
      case 'KeyV': noclipDown = down; return true
      case 'KeyR': if (down) state.wantsReset = true; return true
      default: return false
    }
  }

  window.addEventListener('keydown', (e) => {
    if (setKey(e.code, true)) e.preventDefault()
  })
  window.addEventListener('keyup', (e) => {
    if (setKey(e.code, false)) e.preventDefault()
  })

  window.addEventListener('mousemove', (e) => {
    if (document.pointerLockElement !== canvas) return
    const radPerPixel = sensitivity * M_YAW_RAD
    state.yaw -= e.movementX * radPerPixel
    state.pitch -= e.movementY * radPerPixel
    const limit = Math.PI / 2 - 0.001
    if (state.pitch > limit) state.pitch = limit
    if (state.pitch < -limit) state.pitch = -limit
  })

  document.addEventListener('pointerlockchange', () => {
    const locked = document.pointerLockElement === canvas
    onLockChange?.(locked)
    if (!locked) {
      state.forward = state.back = state.left = state.right = state.jump = false
      state.crouch = state.boost = false
      noclipDown = false
      edges.reset()
    }
  })

  return {
    state,
    consumeTickEdges() {
      state.jumpPressed = edges.pressed('jump', state.jump)
      state.noclipPressed = edges.pressed('noclip', noclipDown)
    },
    isLocked() {
      return document.pointerLockElement === canvas
    },
    getSensitivity() {
      return sensitivity
    },
    setSensitivity(v: number) {
      sensitivity = settings.saveNumber(SENSITIVITY, v)
    },
  }
}
